"use client";

import * as Select from "@radix-ui/react-select";
import { Building2, Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";
import type { TenantLocation } from "@/lib/mock-data";

export interface TenantLocationSelectorProps {
  locations: TenantLocation[];
  value: string;
  onChange: (id: string) => void;
  className?: string;
}

/** Global Shell primitive: tenant/location selector. */
export function TenantLocationSelector({ locations, value, onChange, className }: TenantLocationSelectorProps) {
  const current = locations.find((l) => l.id === value) ?? locations[0];

  return (
    <Select.Root value={value} onValueChange={onChange}>
      <Select.Trigger
        aria-label="Select restaurant location"
        className={cn(
          "flex w-full items-center gap-2 rounded-control border border-border-subtle bg-surface-secondary px-3 py-2.5 text-start",
          "hover:border-border-strong focus-visible:outline-2 focus-visible:outline-brand-primary",
          className,
        )}
      >
        <Building2 aria-hidden="true" size={16} className="shrink-0 text-brand-primary" />
        <span className="min-w-0 flex-1">
          <Select.Value>
            <span className="block truncate text-xs font-bold text-text-primary">{current?.tenantName}</span>
            <span className="block truncate text-[11px] text-text-secondary">{current?.locationName}</span>
          </Select.Value>
        </span>
        <Select.Icon>
          <ChevronDown aria-hidden="true" size={14} className="text-text-secondary" />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          position="popper"
          sideOffset={6}
          className="z-50 w-[--radix-select-trigger-width] overflow-hidden rounded-card border border-border-subtle bg-surface-elevated shadow-[var(--shadow-lifted)]"
        >
          <Select.Viewport className="p-1.5">
            {locations.map((loc) => (
              <Select.Item
                key={loc.id}
                value={loc.id}
                className="flex cursor-pointer items-center justify-between gap-2 rounded-control px-2.5 py-2 text-sm text-text-primary outline-none data-[highlighted]:bg-surface-secondary"
              >
                <span className="min-w-0">
                  <Select.ItemText>
                    <span className="block truncate text-xs font-bold">{loc.tenantName}</span>
                  </Select.ItemText>
                  <span className="block truncate text-[11px] text-text-secondary">{loc.locationName}</span>
                </span>
                <Select.ItemIndicator>
                  <Check aria-hidden="true" size={14} className="text-brand-primary" />
                </Select.ItemIndicator>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}
